"use client"

import { useEffect, useRef } from "react"

interface Particle {
    x: number
    y: number
    size: number
    speedY: number
    speedX: number
    opacity: number
    color: string
}

export function ParticlesBackground() {
    const canvasRef = useRef<HTMLCanvasElement>(null)

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return

        const ctx = canvas.getContext("2d")
        if (!ctx) return

        let animationFrameId: number
        let particles: Particle[] = []

        const resizeCanvas = () => {
            canvas.width = window.innerWidth
            canvas.height = window.innerHeight
        }

        const createParticles = () => {
            const count = Math.min(window.innerWidth / 10, 150) // Adjust density based on screen width
            particles = []
            for (let i = 0; i < count; i++) {
                particles.push({
                    x: Math.random() * canvas.width,
                    y: Math.random() * canvas.height,
                    size: Math.random() * 2 + 0.5,
                    speedY: Math.random() * -0.5 - 0.1, // Moving up slowly
                    speedX: Math.random() * 0.4 - 0.2, // Subtle horizontal drift
                    opacity: Math.random() * 0.5 + 0.1,
                    color: Math.random() > 0.8 ? "#9333ea" : "#f59e0b", // 20% Purple, 80% Gold
                })
            }
        }

        const drawParticles = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height)

            particles.forEach((p) => {
                ctx.beginPath()
                ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
                ctx.fillStyle = p.color
                ctx.globalAlpha = p.opacity
                ctx.shadowBlur = 10
                ctx.shadowColor = p.color
                ctx.fill()

                // Update position
                p.y += p.speedY
                p.x += p.speedX + Math.sin(p.y * 0.01) * 0.2 // Wavy movement

                // Reset if out of bounds
                if (p.y < -10) {
                    p.y = canvas.height + 10
                    p.x = Math.random() * canvas.width
                }
                if (p.x < -10) p.x = canvas.width + 10
                if (p.x > canvas.width + 10) p.x = -10
            })

            // Mouse Interaction (simple push)
            // Implementation skipped for simplicity as it can be resource intensive without throttling

            animationFrameId = requestAnimationFrame(drawParticles)
        }

        resizeCanvas()
        createParticles()
        drawParticles()

        window.addEventListener("resize", () => {
            resizeCanvas()
            createParticles()
        })

        return () => {
            cancelAnimationFrame(animationFrameId)
            window.removeEventListener("resize", resizeCanvas)
        }
    }, [])

    return (
        <canvas
            ref={canvasRef}
            className="absolute inset-0 z-0 pointer-events-none w-full h-full opacity-60"
        />
    )
}
